import { useEffect, useRef } from 'react';
import { usePageContent } from '@/context/ContentContext';
import { telHref } from '@/lib/phone';

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null);
  const { phone } = usePageContent('contact');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  const footerLinks = [
    {
      title: 'Proizvodi',
      links: [
        { name: 'Kućni brojevi', href: '#' },
        { name: 'Pločice za vrata', href: '#' },
        { name: 'Poštansko sanduče', href: '#' },
        { name: 'Zabranjeno parkiranje', href: '#' },
      ],
    },
    {
      title: 'Podrška',
      links: [
        { name: 'Pomoć dizajnera', href: '#' },
        { name: 'Materijali i boje', href: '#' },
        { name: 'Najčešća pitanja', href: '#' },
        { name: 'Uputstvo za postavljanje', href: '#' },
      ],
    },
    {
      title: 'Kompanija',
      links: [
        { name: 'O nama', href: '#about' },
        { name: 'Dostava', href: '#' },
        { name: 'Načini plaćanja', href: '#' },
        { name: 'Kontakt', href: '#contact' },
      ],
    },
    {
      title: 'Pravno',
      links: [
        { name: 'Politika privatnosti', href: '#' },
        { name: 'Uslovi korišćenja', href: '#' },
        { name: 'Politika kolačića', href: '#' },
      ],
    },
  ];

  return (
    <footer id="contact" ref={footerRef} className="w-full bg-primary-800">
      <div className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[1450px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-10 mb-12">
            <div className="lg:col-span-2 reveal opacity-0 translate-y-4 transition-all duration-700 ease-out">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 flex items-center justify-center bg-white rounded">
                  <i className="ri-home-4-line text-primary-800 text-lg" />
                </div>
                <span className="font-heading text-xl font-semibold text-white tracking-tight">
                  Kućni Brojevi
                </span>
              </div>
              <p className="text-sm text-white/80 mb-6 max-w-sm leading-relaxed">
                Sa preko 50.000 izrađenih kućnih brojeva i više od 30.000 zadovoljnih kupaca, naši proizvodi ukrase domove širom Srbije.
              </p>
              {phone && (
                <a
                  href={telHref(phone)}
                  className="inline-flex items-center gap-2 mb-6 px-4 py-2.5 bg-amber-600 text-white font-label text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-fill" />
                  {phone}
                </a>
              )}
              <div className="flex items-center gap-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg text-white/80 hover:border-white hover:text-white transition-colors">
                  <i className="ri-facebook-fill" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg text-white/80 hover:border-white hover:text-white transition-colors">
                  <i className="ri-instagram-line" />
                </a>
                <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg text-white/80 hover:border-white hover:text-white transition-colors">
                  <i className="ri-tiktok-fill" />
                </a>
                <a href="mailto:info@kucnibrojevi.com" className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-lg text-white/80 hover:border-white hover:text-white transition-colors">
                  <i className="ri-mail-line" />
                </a>
              </div>
            </div>

            {footerLinks.map((section, si) => (
              <div key={section.title} className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out" style={{ transitionDelay: `${(si + 1) * 100}ms` }}>
                <h4 className="font-label text-sm font-semibold text-white mb-4">
                  {section.title}
                </h4>
                <ul className="space-y-2.5">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <a
                        href={link.href}
                        className="text-sm font-label text-white/80 hover:text-white transition-colors"
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 reveal opacity-0 translate-y-4 transition-all duration-700 ease-out">
            <p className="text-xs text-white/60">
              &copy; 2026 Kućni Brojevi. Sva prava zadržana.
            </p>
            <div className="flex items-center gap-4 text-xs text-white/60">
              <span>PIB: 123456</span>
              <span>MB: 123456</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </footer>
  );
}